import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { handleError, handleSuccess } from '../../utils';
import { ToastContainer } from 'react-toastify';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Pagination,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Code as CodeIcon,
  Language as LanguageIcon
} from '@mui/icons-material';

import ProgramCard from './ProgramCard';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPrograms, setTotalPrograms] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const axiosConfig = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  };

  const fetchPrograms = async (page = 1, search = '', language = '') => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '9',
        ...(search && { name: search }),
        ...(language && { language: language })
      });

      const url = `${API_URL}/api/programs?${queryParams}`;
      const response = await axios.get(url, axiosConfig);
      const result = response.data;

      if (result.success) {
        setPrograms(result.data);
        setTotalPages(result.pagination.totalPages);
        setCurrentPage(result.pagination.currentPage);
        setTotalPrograms(result.pagination.totalPrograms);
      } else {
        handleError(result.message || 'Failed to fetch programs');
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
      handleError('Failed to fetch programs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (program) => {
    setProgramToDelete(program);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!programToDelete) return;

    try {
      const url = `${API_URL}/api/programs/${programToDelete._id}`;
      const response = await axios.delete(url, axiosConfig);
      const result = response.data;

      if (result.success) {
        handleSuccess('Program deleted successfully');
        fetchPrograms(currentPage, searchTerm, languageFilter);
      } else {
        handleError(result.message || 'Failed to delete program');
      }
    } catch (error) {
      console.error('Error deleting program:', error);
      handleError('Failed to delete program');
    }

    setDeleteDialogOpen(false);
    setProgramToDelete(null);
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPrograms(1, searchTerm, languageFilter);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setLanguageFilter('');
    setCurrentPage(1);
    fetchPrograms(1);
  };

  const handlePageChange = (event, page) => {
    fetchPrograms(page, searchTerm, languageFilter);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          ml: '80px', // Exact sidebar width with no gap
          p: 2,
          backgroundColor: '#ffffff',
          minHeight: '100vh'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="text.secondary">
            Loading programs...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        ml: '80px', // Exact sidebar width with no gap
        p: 2,
        minHeight: '100vh',
        backgroundColor: '#ffffff'
      }}
    >
      <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}
        >
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: 0.5,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              Programs Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalPrograms} programs found
            </Typography>
          </Box>
          
          <Button
            component={Link}
            to="/programs/create"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            ADD NEW PROGRAM
          </Button>
        </Box>

        {/* Search and Filter */}
        <Paper 
          elevation={1}
          sx={{ 
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef'
          }}
        >
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 2,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            <SearchIcon color="primary" />
            Search & Filter Programs
          </Typography>
          
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'flex-end'
            }}
          >
            <TextField
              label="Search programs"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1 }}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
            
            <TextField
              label="Filter by language"
              variant="outlined"
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              sx={{ flex: 1 }}
              size="small"
              InputProps={{
                startAdornment: <LanguageIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
            
            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{ 
                minWidth: 100,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              SEARCH
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ 
                minWidth: 80,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              CLEAR
            </Button>
          </Box>
        </Paper>

        {/* Programs Grid */}
        {programs.length === 0 ? (
          <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <CodeIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="text.secondary">
              No programs found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm || languageFilter
                ? 'Try adjusting your search criteria'
                : 'Get started by creating your first program'
              }
            </Typography>
            <Button
              component={Link}
              to="/programs/create"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2 }}
            >
              Create Your First Program
            </Button>
          </Paper>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {programs.map((program) => (
                <Grid item xs={12} sm={6} lg={4} key={program._id}>
                  <ProgramCard
                    program={program}
                    onDelete={handleDeleteClick}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 1,
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDelete}
          programName={programToDelete?.name}
        />

        <ToastContainer />
      </Box>
    </Box>
  );
};

export default Programs;
